import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ScrollableModal } from '../../components/ui/ScrollableModal';
import { Users, Loader2 } from 'lucide-react';
import { organisationsAPI } from '../../services/api';
import toast from '../../services/toast';
import Input from '../../components/ui/Input';
import FormField from '../../components/forms/FormField';
import SelectDropdown from '../../components/ui/SelectDropdown';

export default function AddMemberModal({ isOpen, onClose, orgId, onSuccess }: any) {
  const [roles, setRoles] = useState<any[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [rolesLoadFailed, setRolesLoadFailed] = useState(false);

  const { register, handleSubmit, reset, watch, control, formState } = useForm({
    defaultValues: { email: '', roleId: '' },
  });

  const { isSubmitting } = formState;
  const emailVal = watch('email');
  const roleIdVal = watch('roleId');

  const emailRegister = register('email', { required: true });

  useEffect(() => {
    if (isOpen && orgId) {
      setLoadingRoles(true);
      setRolesLoadFailed(false);
      organisationsAPI
        .getRoles(orgId)
        .then((list) => {
          setRoles(Array.isArray(list) ? list : []);
          setRolesLoadFailed(false);
        })
        .catch(() => {
          setRoles([]);
          setRolesLoadFailed(true);
          toast.error(
            'Could not load roles. Check that you can view organisation roles, then try again.'
          );
        })
        .finally(() => setLoadingRoles(false));
    }
  }, [isOpen, orgId]);

  useEffect(() => {
    if (!isOpen) {
      reset({ email: '', roleId: '' });
      setRolesLoadFailed(false);
    }
  }, [isOpen, reset]);

  const onSubmit = handleSubmit(async ({ email, roleId }) => {
    const trimmed = String(email).trim().toLowerCase();
    if (!trimmed) {
      toast.error('Email is required');
      return;
    }
    const rid = String(roleId || '').trim();
    if (!rid) {
      toast.error('Role is required');
      return;
    }
    try {
      const invite = await organisationsAPI.inviteMember(orgId, { email: trimmed, roleId: rid });
      reset({ email: '', roleId: '' });
      onClose();
      if (invite?.emailSent) {
        toast.success('Invite sent. An email with the invite link was sent to the recipient.');
      } else {
        toast.success(
          'Invite created. The email could not be sent—share the invite link with the recipient manually.'
        );
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to send invite');
    }
  });

  const handleClose = () => {
    if (!isSubmitting) {
      reset({ email: '', roleId: '' });
      onClose();
    }
  };

  const canSubmit =
    String(emailVal || '').trim() &&
    String(roleIdVal || '').trim() &&
    !loadingRoles &&
    roles.length > 0;

  return (
    <ScrollableModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add member"
      icon={<Users className="w-5 h-5 text-vivid-green-cyan" />}
      disableClose={isSubmitting}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="btn-secondary-mdl disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-member-form"
            disabled={isSubmitting || !canSubmit}
            className="btn-primary-mdl inline-flex items-center gap-2 border-2 border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                Sending...
              </>
            ) : (
              'Send invite'
            )}
          </button>
        </>
      }
    >
      <form id="add-member-form" onSubmit={onSubmit} noValidate>
        <FormField id="member-email" label="Email address" className="mb-4">
          <Input
            type="email"
            id="member-email"
            placeholder="colleague@example.com"
            disabled={isSubmitting}
            autoFocus
            autoComplete="email"
            {...emailRegister}
            onInput={(e) => {
              emailRegister.onChange(e);
            }}
          />
          <p className="mt-1.5 text-xs text-slate-400">
            An invite link will be sent to this email. They can accept to join your organisation.
          </p>
        </FormField>
        <FormField
          id="member-role"
          label={
            <>
              Role <span className="text-red-500">*</span>
            </>
          }
          className="mb-4"
        >
          <Controller
            name="roleId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <SelectDropdown
                id="member-role"
                value={field.value}
                onChange={field.onChange}
                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                placeholder={loadingRoles ? 'Loading roles…' : 'Select a role'}
                disabled={isSubmitting || loadingRoles || roles.length === 0}
              />
            )}
          />
          {!loadingRoles && roles.length === 0 && orgId ? (
            <p className="mt-1.5 text-amber-300" role="status">
              {rolesLoadFailed
                ? 'Roles could not be loaded. Fix permissions or your connection, then reopen this dialog.'
                : 'No roles exist for this organisation yet. Create a role under Configuration → Roles, then try again.'}
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-slate-400">
              The invited user will get this role when they accept.
            </p>
          )}
        </FormField>
      </form>
    </ScrollableModal>
  );
}
